// client/src/components/messaging/MessageReactions.tsx
// Emoji reactions component for messages

import { useState, useRef, useEffect } from 'react';
import { useToggleMessageReactionMutation } from '../../api/uniboxApi';

interface ReactionSummary {
  emoji: string;
  count: number;
  userIds: string[];
}

interface MessageReactionsProps {
  messageId: string;
  reactions?: ReactionSummary[];
  currentUserId?: string;
  compact?: boolean;
}

// Common emoji reactions
const EMOJI_OPTIONS = [
  { emoji: '👍', label: 'Thumbs up' },
  { emoji: '❤️', label: 'Heart' },
  { emoji: '😂', label: 'Laugh' },
  { emoji: '😮', label: 'Wow' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '🔥', label: 'Fire' },
  { emoji: '✅', label: 'Check' },
  { emoji: '👀', label: 'Eyes' },
];

export default function MessageReactions({
  messageId,
  reactions = [],
  currentUserId,
  compact = false,
}: MessageReactionsProps) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [toggleReaction, { isLoading }] = useToggleMessageReactionMutation();

  // Close picker on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    }
    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPicker]);

  const handleReaction = async (emoji: string) => {
    try {
      await toggleReaction({ messageId, emoji }).unwrap();
      setShowPicker(false);
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
    }
  };

  const hasReacted = (emoji: string) => {
    if (!currentUserId) return false;
    const reaction = reactions.find((r) => r.emoji === emoji);
    return reaction?.userIds.includes(currentUserId) ?? false;
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {/* Existing reactions */}
      {reactions
        .filter((r) => r.count > 0)
        .map((reaction) => (
          <button
            key={reaction.emoji}
            onClick={() => handleReaction(reaction.emoji)}
            disabled={isLoading}
            className={`
              inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
              transition-colors border
              ${
                hasReacted(reaction.emoji)
                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                  : 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200'
              }
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            title={`${reaction.count} reaction${reaction.count !== 1 ? 's' : ''}`}
          >
            <span>{reaction.emoji}</span>
            <span>{reaction.count}</span>
          </button>
        ))}

      {/* Add reaction button */}
      <div className="relative" ref={pickerRef}>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className={`
            p-1 rounded-full transition-colors
            ${compact ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
          `}
          title="Add reaction"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>

        {/* Emoji picker */}
        {showPicker && (
          <div className="absolute bottom-full left-0 mb-1 p-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="flex gap-1">
              {EMOJI_OPTIONS.map(({ emoji, label }) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  disabled={isLoading}
                  className={`
                    p-1.5 text-lg rounded hover:bg-gray-100 transition-colors
                    ${hasReacted(emoji) ? 'bg-blue-50' : ''}
                    ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  title={label}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
